import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../app/store";
import { LogInRequest } from "../authModel";
import { getLogInRequest } from "../authentificationSlice";
import { CheckUserData, CreateAccount } from "../../Accounts/accountSlice";
import { useNavigate } from "react-router-dom";
import { GetServerEmailError } from "../../../app/validation";
import { useEmailErrorMessage } from "../../../UIComponents/Form/EmailField";
import { LoadingPage } from "../../CommonPages/loadingPage";
import LoginCard from "./loginCard";
import { useTranslation } from "react-i18next";
import { AlertComponent } from "../../../UIComponents/Alert/AlertComponent";
import { Helmet } from "react-helmet-async";

export function LogIn() {
    const [errorMessage, seterrorMessage] = useState("");
    const [open, setOpen] = useState(false);
    const [email, setEmail] = useState('');
    const [emailError, setEmailError] = useState<string | undefined>();
    const [password, setPassword] = useState('');
    const logInData = useSelector((state: RootState) => state.AuthentificationSlice);
    const userData = useSelector((state: RootState) => state.AccountSlice);
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const getEmailError = useEmailErrorMessage();

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        submitFunction(email, password);
    };

    const submitFunction = (email: string, password: string) => {
        const emailProblem = getEmailError(email);
        if (emailProblem) {
            setEmailError(emailProblem);
            return;
        }
        if (password && password !== "") {
            const logInReq: LogInRequest = {
                username: email,
                password: password
            };
            dispatch(getLogInRequest(logInReq));
        } else {
            seterrorMessage(t("InsertPassword"));
            setOpen(true);
        }
    };

    useEffect(() => {
        if (logInData.error !== null && logInData.error !== '') {
            // A 400 on the email is a fixable typo, so it goes on the field.
            const serverEmailError = GetServerEmailError(logInData.statusCode, logInData.error);
            if (serverEmailError) {
                setEmailError(serverEmailError);
                return;
            }
            if (logInData.error === 'This account is not set up with Single Sign-On (SSO).') {
                seterrorMessage(t("DifferentWayUserLoggedIn") ?? t("AnErrorOccurred"));
            }
            else {
                seterrorMessage(t("IncorrectCredentials") ?? t("AnErrorOccurred"));
            }

            setOpen(true);
        }
        if (logInData.hasLoginData) {
            dispatch(CheckUserData());
        }
    }, [logInData, dispatch, t]);

    useEffect(() => {
        if (userData.accountSliceData.statusValues.statusCode === "403") {
            dispatch(CreateAccount());
        } else if (userData.accountSliceData.statusValues.statusCode === "OK" || userData.accountSliceData.statusValues.statusCode === "200") {
            dispatch(CheckUserData());
        }
    }, [userData.accountSliceData.statusValues.statusCode, dispatch]);

    useEffect(() => {
        if (userData.accountSliceData.statusValues.error !== null) {
            seterrorMessage(userData.accountSliceData.statusValues.error ?? t("AnErrorOccurred"));
            setOpen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData.shouldCreateData, t]);

    useEffect(() => {
        if (logInData.hasLoginData && userData.hasUserData && !userData.shouldCreateData) {
            navigate('/');
        }
    }, [userData, logInData.hasLoginData, navigate]);

    if (logInData.isLoading) {
        return (LoadingPage(logInData.isLoading))
    }

    if (userData.accountSliceData.statusValues.isLoading) {
        return (LoadingPage(userData.accountSliceData.statusValues.isLoading))
    }

    if (logInData.hasLoginData && !userData.hasUserData && userData.shouldCreateData) {
        navigate("/createUserData");
    }

    return (
        <div className="flex justify-center m-[5%]">
            <Helmet>
                <title>BIMAGINE Login and manage your account</title>
                <meta name="description" content="Login and start using our Software Solutions" />
                <link rel="canonical" href={"https://www.bimagine.com/login"} />
            </Helmet>
            <form className="space-y-5" onSubmit={handleSubmit}>
                {errorMessage !== "" ?
                    <AlertComponent open={open} onClose={() => setOpen(false)} message={errorMessage} />
                    : <></>}
                <LoginCard onClickFunction={submitFunction} setEmailForm={setEmail} setPasswordForm={setPassword} emailError={emailError} onEmailErrorClear={() => setEmailError(undefined)} />
                <input type="submit" style={{ display: "none" }} />
            </form>
        </div>
    );
}
